﻿import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';

// --- Icons (Inline SVGs for crispness) ---
const MINDLogo = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="url(#iris-gradient-def)" strokeWidth="2"/>
        <circle cx="12" cy="12" r="4" fill="url(#iris-gradient-def)" />
        <defs>
            <linearGradient id="iris-gradient-def" x1="2" y1="2" x2="22" y2="22" gradientUnits="userSpaceOnUse">
                <stop stopColor="#6366f1"/>
                <stop offset="0.5" stopColor="#8b5cf6"/>
                <stop offset="1" stopColor="#ec4899"/>
            </linearGradient>
        </defs>
    </svg>
);

const UserIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
    </svg>
);

const SendIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
    </svg>
);

// --- Settings Panel Component ---
const SettingsPanel = ({ isOpen, onClose, preferences, onPreferenceChange }) => {
    const handleToggle = (key, value) => {
        onPreferenceChange(key, value);
    };

    const fontSizeOptions = [
        { value: 'small', label: 'Small', description: 'Compact text for more content' },
        { value: 'medium', label: 'Medium', description: 'Standard comfortable reading' },
        { value: 'large', label: 'Large', description: 'Larger text for better visibility' }
    ];

    const languageModeOptions = [
        { value: 'formal', label: 'Professional', description: 'Standard council communication style' },
        { value: 'friendly', label: 'Friendly Assistant', description: 'Warm, conversational style' }
    ];

    const applyPreferences = () => {
        // Apply dark mode
        if (preferences.darkMode) {
            document.documentElement.classList.add('dark');
            document.body.style.backgroundColor = '#0f172a';
        } else {
            document.documentElement.classList.remove('dark');
            document.body.style.backgroundColor = '#f8fafc';
        }

        // Apply font size
        const fontSizes = { small: '14px', medium: '16px', large: '18px' };
        document.documentElement.style.fontSize = fontSizes[preferences.fontSize];
    };

    useEffect(() => {
        applyPreferences();
    }, [preferences]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            ></div>
            
            {/* Settings Panel */}
            <div className="relative bg-white rounded-3xl shadow-2xl border border-indigo-100 w-full max-w-md mx-4 overflow-hidden transform transition-all scale-100">
                
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                                <i className="fas fa-cog text-lg"></i>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold">Settings</h2>
                                <p className="text-indigo-100 text-sm">Customize your MIND experience</p>
                            </div>
                        </div>
                        <button 
                            onClick={onClose}
                            className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                </div>
                
                {/* Content */}
                <div className="p-6 space-y-6 max-h-96 overflow-y-auto">
                    
                    {/* Dark Mode Toggle */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                                    <i className="fas fa-moon text-slate-600"></i>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-slate-900">Dark Mode</h3>
                                    <p className="text-sm text-slate-500">Easier on the eyes in low light</p>
                                </div>
                            </div>
                            <button
                                onClick={() => handleToggle('darkMode', !preferences.darkMode)}
                                className={`relative w-12 h-6 rounded-full transition-colors ${
                                    preferences.darkMode ? 'bg-indigo-600' : 'bg-slate-300'
                                }`}
                            >
                                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                    preferences.darkMode ? 'translate-x-7' : 'translate-x-1'
                                }`}></div>
                            </button>
                        </div>
                    </div>
                    
                    {/* Font Size */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                                <i className="fas fa-font text-slate-600"></i>
                            </div>
                            <div>
                                <h3 className="font-semibold text-slate-900">Text Size</h3>
                                <p className="text-sm text-slate-500">Adjust for comfortable reading</p>
                            </div>
                        </div>
                        <div className="space-y-2">
                            {fontSizeOptions.map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => handleToggle('fontSize', option.value)}
                                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                                        preferences.fontSize === option.value
                                            ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                                            : 'border-slate-200 bg-white hover:bg-slate-50'
                                    }`}
                                >
                                    <div className="font-medium">{option.label}</div>
                                    <div className="text-xs text-slate-500 mt-0.5">{option.description}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    {/* Language Mode */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                                <i className="fas fa-comments text-slate-600"></i>
                            </div>
                            <div>
                                <h3 className="font-semibold text-slate-900">Chat Style</h3>
                                <p className="text-sm text-slate-500">How MIND talks to you</p>
                            </div>
                        </div>
                        <div className="space-y-2">
                            {languageModeOptions.map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => handleToggle('languageMode', option.value)}
                                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                                        preferences.languageMode === option.value
                                            ? 'border-purple-200 bg-purple-50 text-purple-700'
                                            : 'border-slate-200 bg-white hover:bg-slate-50'
                                    }`}
                                >
                                    <div className="font-medium flex items-center gap-2">
                                        {option.label}
                                        {option.value === 'neighbour' && (
                                            <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">New!</span>
                                        )}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5">{option.description}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    {/* Smart Features */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                                <i className="fas fa-magic text-slate-600"></i>
                            </div>
                            <div>
                                <h3 className="font-semibold text-slate-900">Smart Features</h3>
                                <p className="text-sm text-slate-500">Intelligent assistance options</p>
                            </div>
                        </div>
                        
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-medium text-slate-700">Auto-suggestions</div>
                                    <div className="text-xs text-slate-500">Show quick reply buttons</div>
                                </div>
                                <button
                                    onClick={() => handleToggle('autoSuggest', !preferences.autoSuggest)}
                                    className={`relative w-12 h-6 rounded-full transition-colors ${
                                        preferences.autoSuggest ? 'bg-green-500' : 'bg-slate-300'
                                    }`}
                                >
                                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                        preferences.autoSuggest ? 'translate-x-7' : 'translate-x-1'
                                    }`}></div>
                                </button>
                            </div>
                            
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-medium text-slate-700">Notifications</div>
                                    <div className="text-xs text-slate-500">Service alerts and updates</div>
                                </div>
                                <button
                                    onClick={() => handleToggle('notifications', !preferences.notifications)}
                                    className={`relative w-12 h-6 rounded-full transition-colors ${
                                        preferences.notifications ? 'bg-green-500' : 'bg-slate-300'
                                    }`}
                                >
                                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                        preferences.notifications ? 'translate-x-7' : 'translate-x-1'
                                    }`}></div>
                                </button>
                            </div>
                        </div>
                    </div>
                    
                </div>
                
                {/* Footer */}
                <div className="bg-slate-50 p-4 text-center border-t border-slate-100">
                    <p className="text-xs text-slate-500 flex items-center justify-center gap-2">
                        <i className="fas fa-save"></i>
                        Settings saved automatically
                    </p>
                </div>
            </div>
        </div>
    );
};

// --- Wizard Definitions ---
const WIZARDS = {
    'bin-collection-report': {
        title: 'Report Missed Bin Collection',
        icon: 'fa-trash-alt',
        steps: [
            {
                id: 'address',
                title: 'Your Address',
                question: 'What\'s your full address?',
                type: 'text',
                placeholder: 'e.g., 123 Business Park, City, Postcode',
                required: true
            },
            {
                id: 'collection-type',
                title: 'Collection Type',
                question: 'Which bin was missed?',
                type: 'radio',
                options: [
                    { value: 'general', label: 'General Waste (Black bin)' },
                    { value: 'recycling', label: 'Recycling (Blue bin)' },
                    { value: 'garden', label: 'Garden Waste (Brown bin)' },
                    { value: 'food', label: 'Food Waste (Small green caddy)' }
                ],
                required: true
            },
            {
                id: 'collection-date',
                title: 'Collection Date',
                question: 'When was it supposed to be collected?',
                type: 'date',
                required: true
            },
            {
                id: 'additional-info',
                title: 'Additional Information',
                question: 'Any additional details? (optional)',
                type: 'textarea',
                placeholder: 'e.g., bin was out by 7am, weather conditions, etc.',
                required: false
            }
        ]
    },
    'parking-permit': {
        title: 'Apply for Parking Permit',
        icon: 'fa-car',
        steps: [
            {
                id: 'permit-type',
                title: 'Permit Type',
                question: 'What type of parking permit do you need?',
                type: 'radio',
                options: [
                    { value: 'resident', label: 'Resident Parking Permit' },
                    { value: 'visitor', label: 'Visitor Permit' },
                    { value: 'business', label: 'Business Permit' },
                    { value: 'temporary', label: 'Temporary Permit' }
                ],
                required: true
            },
            {
                id: 'address',
                title: 'Property Address',
                question: 'What\'s the address for the permit?',
                type: 'text',
                placeholder: 'Full address including postcode',
                required: true
            },
            {
                id: 'vehicle-reg',
                title: 'Vehicle Registration',
                question: 'What\'s the vehicle registration number?',
                type: 'text',
                placeholder: 'e.g., AB12 CDE',
                required: true
            },
            {
                id: 'contact-details',
                title: 'Contact Details',
                question: 'Your contact information',
                type: 'group',
                fields: [
                    { id: 'name', label: 'Full Name', type: 'text', required: true },
                    { id: 'phone', label: 'Phone Number', type: 'tel', required: true },
                    { id: 'email', label: 'Email Address', type: 'email', required: true }
                ]
            }
        ]
    },
    'report-issue': {
        title: 'Report a Community Issue',
        icon: 'fa-exclamation-triangle',
        steps: [
            {
                id: 'issue-type',
                title: 'Issue Type',
                question: 'What type of issue are you reporting?',
                type: 'radio',
                options: [
                    { value: 'pothole', label: 'Pothole or Road Surface' },
                    { value: 'streetlight', label: 'Street Lighting' },
                    { value: 'fly-tipping', label: 'Fly-tipping' },
                    { value: 'graffiti', label: 'Graffiti or Vandalism' },
                    { value: 'dog-fouling', label: 'Dog Fouling' },
                    { value: 'other', label: 'Other Issue' }
                ],
                required: true
            },
            {
                id: 'location',
                title: 'Location',
                question: 'Where is the issue located?',
                type: 'text',
                placeholder: 'e.g., Outside 15 High Street, near the post office',
                required: true
            },
            {
                id: 'description',
                title: 'Description',
                question: 'Please describe the issue in detail',
                type: 'textarea',
                placeholder: 'Provide as much detail as possible to help us resolve the issue quickly',
                required: true
            },
            {
                id: 'photo-upload',
                title: 'Photo Evidence',
                question: 'Can you provide a photo? (optional but helpful)',
                type: 'info',
                content: 'After submitting this report, you\'ll receive instructions on how to email a photo to the relevant department.',
                required: false
            }
        ]
    }
};

// Multi-Step Wizard Component
const WizardStep = ({ wizard, stepIndex, wizardData, onUpdate, onNext, onPrev, onComplete, onCancel }) => {
    const step = wizard.steps[stepIndex];
    const isLastStep = stepIndex === wizard.steps.length - 1;
    
    const handleFieldUpdate = (fieldId, value) => {
        onUpdate({
            ...wizardData,
            [fieldId]: value
        });
    };
    
    const handleNext = () => {
        if (isLastStep) {
            onComplete();
        } else {
            onNext();
        }
    };
    
    return (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-5 mt-4">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {stepIndex + 1}
                </div>
                <div>
                    <h4 className="font-bold text-slate-800">{step.title}</h4>
                    <p className="text-xs text-slate-500">Step {stepIndex + 1} of {wizard.steps.length}</p>
                </div>
            </div>
            
            <p className="text-slate-700 mb-4 font-medium">{step.question}</p>
            
            {step.type === 'text' && (
                <input
                    type="text"
                    placeholder={step.placeholder}
                    value={wizardData[step.id] || ''}
                    onChange={(e) => handleFieldUpdate(step.id, e.target.value)}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 mb-4"
                    autoFocus
                />
            )}
            
            {step.type === 'textarea' && (
                <textarea
                    placeholder={step.placeholder}
                    value={wizardData[step.id] || ''}
                    onChange={(e) => handleFieldUpdate(step.id, e.target.value)}
                    rows="3"
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 mb-4"
                    autoFocus
                />
            )}
            
            {step.type === 'radio' && (
                <div className="space-y-2 mb-4">
                    {step.options.map((option) => (
                        <label key={option.value} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-white cursor-pointer">
                            <input
                                type="radio"
                                name={step.id}
                                value={option.value}
                                checked={wizardData[step.id] === option.value}
                                onChange={(e) => handleFieldUpdate(step.id, e.target.value)}
                                className="text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-slate-700">{option.label}</span>
                        </label>
                    ))}
                </div>
            )}
            
            {step.type === 'date' && (
                <input
                    type="date"
                    value={wizardData[step.id] || ''}
                    onChange={(e) => handleFieldUpdate(step.id, e.target.value)}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 mb-4"
                    autoFocus
                />
            )}
            
            {step.type === 'group' && (
                <div className="space-y-3 mb-4">
                    {step.fields.map((field) => (
                        <div key={field.id}>
                            <label className="block text-sm font-medium text-slate-700 mb-1">{field.label}</label>
                            <input
                                type={field.type}
                                value={wizardData[field.id] || ''}
                                onChange={(e) => handleFieldUpdate(field.id, e.target.value)}
                                required={field.required}
                                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>
                    ))}
                </div>
            )}
            
            {step.type === 'info' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                        <i className="fas fa-info-circle text-blue-600 mt-1"></i>
                        <p className="text-blue-800">{step.content}</p>
                    </div>
                </div>
            )}
            
            <div className="flex justify-between items-center pt-4 border-t border-slate-200">
                <div className="flex gap-2">
                    {stepIndex > 0 && (
                        <button
                            onClick={onPrev}
                            className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium"
                        >
                            <i className="fas fa-chevron-left mr-1"></i>
                            Previous
                        </button>
                    )}
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-red-600 hover:text-red-800 font-medium"
                    >
                        Cancel
                    </button>
                </div>
                
                <button
                    onClick={handleNext}
                    disabled={step.required && !wizardData[step.id]}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed font-medium"
                >
                    {isLastStep ? 'Complete' : 'Next'}
                    <i className={`fas ${isLastStep ? 'fa-check' : 'fa-chevron-right'} ml-1`}></i>
                </button>
            </div>
        </div>
    );
};

// --- Components ---

const ChatMessage = ({ msg, userPrefs, onQuickAction, onStartWizard, onSend }) => {
    const isUser = msg.sender === 'user';
    
    // Process text to convert [Link Text](url) to actual clickable links
    const processText = (text) => {
        if (!text) return '';
        // Convert markdown-style links to HTML (same tab, not target="_blank")
        return text.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, '<a href="$2" class="text-indigo-600 hover:text-indigo-800 underline font-medium">$1</a>');
    };
    
    // Context-Aware Action Button Generation (B2B Sales Focus)
    const generateActionButtons = (messageText) => {
        if (!messageText || isUser || !userPrefs?.autoSuggest) return [];
        
        const text = messageText.toLowerCase();
        const buttons = [];
        
        // Demo/Booking actions
        if (text.includes('demo') || text.includes('consultation') || text.includes('meeting') || text.includes('schedule') || text.includes('book')) {
            buttons.push({ icon: 'fa-calendar-check', label: 'Book Demo', action: 'book-demo' });
            if (text.includes('available') || text.includes('time') || text.includes('when')) {
                buttons.push({ icon: 'fa-clock', label: 'View Availability', action: 'view-availability' });
            }
        }
        
        // Pricing/Cost actions
        if (text.includes('price') || text.includes('cost') || text.includes('pricing') || text.includes('plan') || text.includes('tier') || text.includes('Â£') || text.includes('$')) {
            buttons.push({ icon: 'fa-tags', label: 'View Pricing', action: 'view-pricing' });
            if (text.includes('save') || text.includes('roi') || text.includes('savings')) {
                buttons.push({ icon: 'fa-calculator', label: 'Calculate ROI', action: 'calculate-roi' });
            }
        }
        
        // Feature/Product actions
        if (text.includes('feature') || text.includes('capability') || text.includes('integration') || text.includes('how does') || text.includes('can it')) {
            buttons.push({ icon: 'fa-rocket', label: 'See Features', action: 'view-features' });
            if (text.includes('demo') || text.includes('show me') || text.includes('example')) {
                buttons.push({ icon: 'fa-play-circle', label: 'Live Demo', action: 'live-demo' });
            }
        }
        
        // Industry/Use Case actions
        if (text.includes('industry') || text.includes('retail') || text.includes('healthcare') || text.includes('automotive') || 
            text.includes('hospitality') || text.includes('e-commerce') || text.includes('use case')) {
            buttons.push({ icon: 'fa-building', label: 'Industry Solutions', action: 'industry-solutions' });
            buttons.push({ icon: 'fa-star', label: 'Success Stories', action: 'success-stories' });
        }
        
        // Technical/Integration actions
        if (text.includes('integrate') || text.includes('api') || text.includes('crm') || text.includes('technical') || text.includes('implementation')) {
            buttons.push({ icon: 'fa-code', label: 'Technical Docs', action: 'technical-docs' });
            buttons.push({ icon: 'fa-puzzle-piece', label: 'Integrations', action: 'view-integrations' });
        }
        
        // Sales/Contact actions
        if (text.includes('contact') || text.includes('sales') || text.includes('speak to') || text.includes('talk to')) {
            buttons.push({ icon: 'fa-calendar-check', label: 'Book Call', action: 'book-demo' });
            buttons.push({ icon: 'fa-envelope', label: 'Email Sales', action: 'email-sales' });
        }
        
        // Limit to 3 most relevant buttons
        return buttons.slice(0, 3);
    };
    
    const actionButtons = generateActionButtons(msg.text);
    
    return (
        <div className={`flex w-full mb-6 ${isUser ? 'justify-end' : 'justify-start'}`}>
            {!isUser && (
                <div className="w-8 h-8 rounded-full bg-white border border-indigo-100 flex items-center justify-center mr-3 shadow-sm shrink-0 mt-1">
                    <MINDLogo className="w-5 h-5" />
                </div>
            )}
            
            <div className={`max-w-[85%] md:max-w-[70%] rounded-2xl shadow-sm ${
                isUser 
                    ? 'bg-iris-gradient text-white rounded-br-none p-4' 
                    : 'bg-white text-slate-800 rounded-bl-none border border-indigo-50 shadow-md ring-1 ring-black/5'
            }`}>
                <div className={isUser ? '' : 'p-4'}>
                    {msg.type === 'action' && (
                        <div className="flex items-center gap-2 border-b border-indigo-100 pb-2 mb-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-indigo-500">
                                <i className="fa-solid fa-bolt mr-1"></i> Action Taken
                            </span>
                        </div>
                    )}
                    
                    <div className="prose prose-sm max-w-none text-inherit leading-relaxed" dangerouslySetInnerHTML={{ __html: processText(msg.text) }} />

                    {/* Retry button for failed messages */}
                    {msg.type === 'error' && msg.canRetry && msg.originalMessage && (
                        <div className="mt-3 pt-2 border-t border-red-200">
                            <button 
                                onClick={() => window.handleRetry(msg.originalMessage)}
                                className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded-full transition-colors"
                            >
                                <i className="fa-solid fa-redo mr-1"></i>
                                Try Again
                            </button>
                        </div>
                    )}

                    {msg.content && (
                        <div className="mt-3 bg-slate-50 rounded-lg p-2 text-xs border border-slate-100 grid grid-cols-2 gap-2 text-slate-600">
                            <div>
                                <span className="block font-bold uppercase text-[10px] text-slate-400">Dept</span>
                                {msg.content.dept}
                            </div>
                            <div>
                                <span className="block font-bold uppercase text-[10px] text-slate-400">Status</span>
                                {msg.content.priority}
                            </div>
                        </div>
                    )}
                    
                    {/* Context-Aware Quick Reply Buttons */}
                    {!isUser && actionButtons.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-slate-100 hidden md:block">
                            <div className="flex flex-wrap gap-2">
                                {actionButtons.map((button, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => onQuickAction(button.action, msg.text)}
                                        className="group flex items-center gap-2 px-3 py-2 text-xs font-medium bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 border border-indigo-200 text-indigo-700 rounded-full transition-all duration-200 hover:shadow-sm hover:scale-105 active:scale-95"
                                    >
                                        <i className={`fas ${button.icon} text-indigo-600 group-hover:text-indigo-700`}></i>
                                        <span>{button.label}</span>
                                        <i className="fas fa-chevron-right text-[10px] text-indigo-400 group-hover:text-indigo-600 transition-colors"></i>
                                    </button>
                                ))}
                            </div>
                            <p className="text-[10px] text-slate-400 mt-2 flex items-center gap-1">
                                <i className="fas fa-magic text-indigo-300"></i>
                                Smart actions based on your conversation
                            </p>
                        </div>
                    )}
                    
                    {/* Wizard Restart Buttons for completed wizards */}
                    {!isUser && (msg.type === 'wizard-complete') && (
                        <div className="mt-4 pt-3 border-t border-green-100 bg-green-50 rounded-lg p-3">
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => onStartWizard(msg.wizardType)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all duration-200 hover:shadow-sm"
                                >
                                    <i className="fas fa-redo text-white"></i>
                                    <span>Start Over</span>
                                </button>
                                <button
                                    onClick={() => onSend("I need to report a different issue")}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-lg transition-all duration-200 hover:shadow-sm"
                                >
                                    <i className="fas fa-plus text-slate-600"></i>
                                    <span>Report Something Else</span>
                                </button>
                            </div>
                            <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                                <i className="fas fa-check-circle text-green-500"></i>
                                Form completed successfully - need to make changes or report something new?
                            </p>
                        </div>
                    )}
                </div>

                {/* RAG Sources Display */}
                {!isUser && msg.sources && msg.sources.length > 0 && (
                    <div className="border-t border-slate-100 bg-gradient-to-br from-indigo-50/50 to-purple-50/30 px-4 py-3 mt-2">
                        <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            <h5 className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Information Sources</h5>
                        </div>
                        <div className="space-y-2">
                            {msg.sources.map((source, idx) => (
                                <a 
                                    key={idx}
                                    href={source.uri} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="group flex items-start gap-2 p-2 rounded-lg hover:bg-white/60 transition-all border border-transparent hover:border-indigo-200 hover:shadow-sm"
                                >
                                    <div className="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                                        <i className="fa-solid fa-link text-indigo-600 text-xs"></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-slate-700 group-hover:text-indigo-700 line-clamp-2 leading-snug">
                                            {source.title}
                                        </p>
                                        <p className="text-[10px] text-slate-500 truncate mt-0.5 group-hover:text-indigo-500">
                                            {new URL(source.uri).hostname}
                                        </p>
                                    </div>
                                    <i className="fa-solid fa-external-link-alt text-indigo-400 text-[10px] mt-1 opacity-0 group-hover:opacity-100 transition-opacity"></i>
                                </a>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {isUser && (
                <div className="w-8 h-8 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center ml-3 shadow-sm shrink-0 mt-1">
                    <UserIcon className="w-5 h-5 text-indigo-400" />
                </div>
            )}
        </div>
    );
};

// MenuOverlay component removed for cleaner mobile experience

// SuggestionChip Component - Reusable chip buttons for suggested queries
const SuggestionChip = ({ icon, label, onClick }) => (
    <button 
        onClick={onClick}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-indigo-100 rounded-full text-sm font-medium text-slate-600 shadow-sm hover:border-indigo-300 hover:text-indigo-600 hover:shadow-md transition-all whitespace-nowrap"
    >
        <i className={`fa-solid ${icon} text-indigo-400`}></i>
        {label}
    </button>
);

// --- Main App ---

function App() {
    // Load preferences first to determine initial message style
    const getInitialPrefs = () => {
        const saved = localStorage.getItem('mind_user_preferences');
        return saved ? JSON.parse(saved) : {
            darkMode: false,
            fontSize: 'medium',
            languageMode: 'formal',
            notifications: true,
            autoSuggest: true
        };
    };
    
    const initialPrefs = getInitialPrefs();
    
    const INITIAL_MSG = { 
        sender: 'ai', 
        type: 'text', 
        text: initialPrefs.languageMode === 'friendly' 
            ? "Hi there! I'm Iris, your AI assistant from AskMind. I'm here to help you explore our products, answer any questions, and schedule a demo. Whether you're looking to automate customer service, streamline operations, or boost sales - I've got you covered. What would you like to know?"
            : "Hello! I'm Iris, AskMind's AI sales assistant. I can help you explore our enterprise AI solutions, discuss pricing and features, schedule personalized demos, and answer technical questions. What brings you here today?"
    };

    const [inputText, setInputText] = useState("");
    
    // Lazy load from Local Storage
    const [chatHistory, setChatHistory] = useState(() => {
        const saved = localStorage.getItem('mind_chat_history');
        return saved ? JSON.parse(saved) : [INITIAL_MSG];
    });

    const [isTyping, setIsTyping] = useState(false);
    const [userHasScrolled, setUserHasScrolled] = useState(false);
    const messagesEndRef = useRef(null);
    
    // Multi-Step Wizard State Management
    const [activeWizard, setActiveWizard] = useState(null);
    const [wizardData, setWizardData] = useState({});
    
    // User Preferences State Management
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [userPrefs, setUserPrefs] = useState(initialPrefs);









    const chatContainerRef = useRef(null);
    const previousMessageCountRef = useRef(chatHistory.length);
    
    // Save to Local Storage whenever chatHistory changes
    useEffect(() => {

        localStorage.setItem('mind_chat_history', JSON.stringify(chatHistory));
        
        // Only auto-scroll if:
        // 1. User hasn't manually scrolled away, OR
        // 2. A new message was just added (count increased)
        const messageCountIncreased = chatHistory.length > previousMessageCountRef.current;
        previousMessageCountRef.current = chatHistory.length;
        
        if (!userHasScrolled || messageCountIncreased) {
            scrollToNewMessage();
            setUserHasScrolled(false);
        }
    }, [chatHistory]);



    const scrollToNewMessage = () => {
        // Scroll to the last message (top of the newest reply)
        if (chatContainerRef.current) {
            const messages = chatContainerRef.current.querySelectorAll('[data-message-index]');
            if (messages.length > 0) {
                const lastMessage = messages[messages.length - 1];
                lastMessage.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }
    };
    
    // Detect when user manually scrolls
    const handleScroll = (e) => {
        const element = e.target;
        const isAtBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 10;
        
        if (!isAtBottom) {
            setUserHasScrolled(true);
        } else {
            setUserHasScrolled(false);
        }
    };

    const handleClearChat = () => {
        if(confirm("Are you sure you want to clear your chat history?")) {
            setChatHistory([INITIAL_MSG]);
            localStorage.removeItem('mind_chat_history');
        }
    };

    // Global retry handler for failed messages
    window.handleRetry = (originalMessage) => {
        // Remove the last error message and retry
        const newHistory = chatHistory.slice(0, -1);
        setChatHistory(newHistory);
        handleSend(originalMessage);
    };

    // Context-Aware Quick Action Handler
    const handleQuickAction = (action, messageText) => {
        console.log('ðŸŽ¯ Quick Action:', action, 'from message:', messageText);
        
        switch (action) {
            case 'book-tickets':
                // Look for event links in the message and open ticket booking
                const eventMatch = messageText.match(/\[([^\]]+)\]\(([^\)]+)\)/);
                if (eventMatch && eventMatch[2].includes('event.html')) {
                    window.open(eventMatch[2] + '&action=book', '_blank');
                } else {
                    handleSend("How can I book tickets for this event?");
                }
                break;
                
            case 'add-calendar':
                // Generate calendar action
                handleSend("Can you help me add this event to my calendar with all the details?");
                break;
                
            case 'book-demo':
                // Open booking calendar
                window.open('/calendar.html', '_blank');
                break;
                
            case 'view-availability':
                // Show calendar
                window.open('/calendar.html', '_blank');
                break;
                
            case 'view-pricing':
                // Open websites page
                window.open('/websites.html', '_blank');
                break;
                
            case 'calculate-roi':
                // Send ROI question
                handleSend("Can you help me calculate the ROI for my business?");
                break;
                
            case 'view-features':
                // Open AI videos page
                window.open('/aivideos.html', '_blank');
                break;
                
            case 'live-demo':
                // Open AI videos page
                window.open('/aivideos.html', '_blank');
                break;
                
            case 'industry-solutions':
                // Ask about industry
                handleSend("What industry solutions do you have?");
                break;
                
            case 'success-stories':
                // Open calendar page
                window.open('/calendar.html', '_blank');
                break;
                
            case 'technical-docs':
                // Request technical information
                handleSend("I need technical documentation about integration and APIs");
                break;
                
            case 'view-integrations':
                // Ask about integrations
                handleSend("What CRM and business tools does AskMind integrate with?");
                break;
                
            case 'email-sales':
                // Open email to sales
                window.location.href = 'mailto:sales@askmind.co.uk?subject=Sales Inquiry from Chat';
                break;
                
            default:
                console.log('Unknown action:', action);
                break;
        }
    };

    // Wizard Management Functions
    const startWizard = (wizardType) => {
        const wizard = WIZARDS[wizardType];
        if (!wizard) return;
        
        setActiveWizard({
            type: wizardType,
            wizard: wizard,
            currentStep: 0
        });
        setWizardData({});
        
        // Add wizard initiation message to chat
        const wizardMsg = {
            sender: 'ai',
            type: 'wizard-start',
            text: `I'll help you ${wizard.title.toLowerCase()}. Let's go through this step by step to make sure we get all the information needed.`,
            wizardType: wizardType
        };
        setChatHistory(prev => [...prev, wizardMsg]);
    };
    
    const updateWizardData = (newData) => {
        setWizardData(newData);
    };
    
    const nextWizardStep = () => {
        if (activeWizard && activeWizard.currentStep < activeWizard.wizard.steps.length - 1) {
            setActiveWizard(prev => ({
                ...prev,
                currentStep: prev.currentStep + 1
            }));
        }
    };
    
    const prevWizardStep = () => {
        if (activeWizard && activeWizard.currentStep > 0) {
            setActiveWizard(prev => ({
                ...prev,
                currentStep: prev.currentStep - 1
            }));
        }
    };
    
    const completeWizard = () => {
        if (!activeWizard) return;
        
        const wizard = activeWizard.wizard;
        const summary = generateWizardSummary();
        
        // Add completion message to chat
        const completionMsg = {
            sender: 'ai',
            type: 'wizard-complete',
            text: `Perfect! I've gathered all the information for your ${wizard.title.toLowerCase()}. Here's your submission summary:\n\n${summary}\n\nI'll now provide you with the contact details and next steps.`,
            wizardData: wizardData,
            wizardType: activeWizard.type
        };
        
        setChatHistory(prev => [...prev, completionMsg]);
        
        // Clear wizard state
        setActiveWizard(null);
        setWizardData({});
        
        // Auto-send follow-up for next steps
        setTimeout(() => {
            handleSend(`Please provide the contact details and submission process for ${wizard.title.toLowerCase()}`);
        }, 1000);
    };
    
    const cancelWizard = () => {
        const cancelMsg = {
            sender: 'ai',
            type: 'text',
            text: "No problem! I've cancelled the form. Feel free to ask me anything else or start the process again later."
        };
        setChatHistory(prev => [...prev, cancelMsg]);
        setActiveWizard(null);
        setWizardData({});
    };
    
    const generateWizardSummary = () => {
        if (!activeWizard) return '';
        
        const wizard = activeWizard.wizard;
        let summary = '';
        
        wizard.steps.forEach(step => {
            if (wizardData[step.id]) {
                summary += `**${step.title}:** ${wizardData[step.id]}\n`;
            }
            
            // Handle group fields
            if (step.type === 'group' && step.fields) {
                step.fields.forEach(field => {
                    if (wizardData[field.id]) {
                        summary += `**${field.label}:** ${wizardData[field.id]}\n`;
                    }
                });
            }
        });
        
        return summary;
    };

    // Interactive Map Integration
    const openMapView = (messageText) => {
        const mapModal = createMapModal(messageText);
        document.body.appendChild(mapModal);
        
        // Initialize map after modal is in DOM
        setTimeout(() => {
            initializeChatMap(messageText);
        }, 100);
    };
    
    const createMapModal = (messageText) => {
        const modal = document.createElement('div');
        modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
        modal.id = 'chat-map-modal';
        
        modal.innerHTML = `
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
                <div class="flex items-center justify-between p-4 border-b border-slate-200">
                    <h3 class="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <i class="fas fa-map-marker-alt text-indigo-600"></i>
                        Locations Map
                    </h3>
                    <button onclick="closeChatMap()" class="text-slate-500 hover:text-slate-700 text-xl">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div id="chat-map" class="w-full h-96"></div>
                <div class="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-t border-slate-200">
                    <div class="flex items-center gap-2 text-sm text-slate-600">
                        <i class="fas fa-info-circle text-indigo-500"></i>
                        <span>Interactive map showing locations mentioned in our conversation</span>
                    </div>
                </div>
            </div>
        `;
        
        // Close on backdrop click
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                closeChatMap();
            }
        });
        
        return modal;
    };
    
    let chatMap = null;
    
    const initializeChatMap = (messageText) => {
        const mapCenter = [51.5074, -0.1278]; // Default to London
        
        chatMap = L.map('chat-map', {
            center: mapCenter,
            zoom: 15,
            zoomControl: true
        });
        
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 19
        }).addTo(chatMap);
        
        // Add markers for mentioned locations
        addLocationMarkers(messageText);
    };
    
    const addLocationMarkers = (messageText) => {
        const locations = extractLocationsFromText(messageText);
        
        locations.forEach(location => {
            const marker = L.marker(location.coords, {
                icon: L.divIcon({
                    html: `<div class="${location.color} text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg border-2 border-white">
                             <i class="fas ${location.icon} text-sm"></i>
                           </div>`,
                    className: 'custom-div-icon',
                    iconSize: [40, 40],
                    iconAnchor: [20, 40]
                })
            }).addTo(chatMap);
            
            marker.bindPopup(`
                <div class="text-center p-2">
                    <h4 class="font-bold text-slate-800 mb-1">${location.name}</h4>
                    <p class="text-sm text-slate-600 mb-2">${location.description}</p>
                    <button onclick="window.open('https://www.google.com/maps/dir//${encodeURIComponent(location.name)}', '_blank')" 
                            class="px-3 py-1 bg-green-600 text-white text-xs rounded-full hover:bg-green-700 transition-colors">
                        <i class="fas fa-directions mr-1"></i>Directions
                    </button>
                </div>
            `);
        });
        
        // Fit map to show all markers
        if (locations.length > 1) {
            const group = new L.featureGroup(locations.map(loc => L.marker(loc.coords)));
            chatMap.fitBounds(group.getBounds(), { padding: [20, 20] });
        }
    };
    
    const extractLocationsFromText = (text) => {
        // Location extraction disabled - return empty array
        return [];
    };
    
    window.closeChatMap = () => {
        const modal = document.getElementById('chat-map-modal');
        if (modal) {
            if (chatMap) {
                chatMap.remove();
                chatMap = null;
            }
            modal.remove();
        }
    };

    const handleSend = async (text) => {
        if (!text.trim()) return;
        
        const startTime = Date.now();
        
        // Add user message
        const newHistory = [...chatHistory, { sender: 'user', type: 'text', text: text }];
        setChatHistory(newHistory);
        setInputText("");
        setIsTyping(true);
        


        // Build API history from chatHistory (exclude initial greeting)
        const apiHistory = chatHistory
            .slice(1) // Skip initial greeting
            .map(msg => ({
                role: msg.sender === 'user' ? 'user' : 'model',
                parts: [{ text: msg.text }]
            }));

        // Add current user message
        apiHistory.push({
            role: 'user',
            parts: [{ text: text }]
        });

        // Get current date for AI context
        const today = new Date();
        const dateString = today.toLocaleDateString('en-GB', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });

        // Build system instruction based on user preferences
        const languageStyle = userPrefs.languageMode === 'friendly' ? 
            "LANGUAGE STYLE: Friendly Assistant Mode - Be warm, conversational, and approachable. Use casual but professional language. Think of yourself as a helpful sales advisor who genuinely wants to find the right solution for each business." :
            "LANGUAGE STYLE: Professional Mode - Maintain a polished, B2B-appropriate tone. Be clear, consultative, and business-focused while remaining personable and helpful.";

        const systemInstruction = {
            role: "system",
            parts: [{
                text: "CURRENT DATE & TIME CONTEXT:\\nToday is " + dateString + ". Always use this date when discussing availability, scheduling demos, or time-sensitive offers.\\n\\n" + languageStyle + "\\n\\nYou are Iris, the AI sales assistant for AskMind - an enterprise AI platform that helps businesses automate customer service, sales, and operations. Your role is to help prospects explore our products, answer questions, and book demos.\\n\\n=== YOUR PRODUCTS ===\\n\\nAskMind offers Iris AI - an intelligent conversation platform with these capabilities:\\n- 24/7 Automated Customer Service - Handle inquiries, resolve issues, provide instant support\\n- Sales Automation - Qualify leads, answer product questions, schedule demos, collect information\\n- Multi-Channel Support - Web chat, SMS, email, social media integration\\n- Business Intelligence - Analytics, sentiment analysis, conversation insights\\n- CRM Integration - Seamless connection with Salesforce, HubSpot, and other tools\\n- Custom Training - Train Iris on your specific products, services, and business processes\\n\\nIndustry Solutions:\\n- Retail & E-commerce: Product recommendations, order tracking, inventory questions\\n- Automotive: Vehicle inquiries, test drive booking, service scheduling\\n- Healthcare: Appointment scheduling, patient information, insurance verification\\n- Hospitality: Reservations, room service, guest services\\n- Financial Services: Account inquiries, application status, product information\\n- Professional Services: Consultation booking, quote requests, service information\\n\\nPricing Tiers (guide prospects to pricing.html for details):\\n- Starter: Â£499/mo - Up to 1000 conversations, basic features, email support\\n- Professional: Â£999/mo - Up to 5000 conversations, advanced AI, priority support, CRM integration\\n- Enterprise: Custom pricing - Unlimited conversations, white-label, dedicated support, custom integrations\\n\\nKey Benefits:\\n- 70% Cost Reduction compared to traditional support teams\\n- <2 Second Response Time for customer inquiries\\n- 98% Accuracy Rate in handling common questions\\n- 24/7 Availability with no downtime\\n- ROI typically within 3-6 months\\n\\n=== YOUR SALES APPROACH ===\\n\\n1. QUALIFY THE PROSPECT:\\n   - What industry are they in?\\n   - What's their main pain point? (e.g., high support costs, slow response times, scaling issues)\\n   - Current team size and volume of customer interactions\\n   - Timeline for implementation\\n\\n2. MATCH SOLUTION TO NEED:\\n   - If they mention high costs: Emphasize 70% cost reduction\\n   - If they mention slow responses: Highlight <2s response time and 24/7 availability\\n   - If they mention scaling: Discuss unlimited capacity and easy expansion\\n   - If they're specific industry: Explain how our solutions have helped similar businesses\\n\\n3. HANDLE COMMON OBJECTIONS:\\n   - Too expensive: Explain ROI calculator and typical 3-6 month payback\\n   - Will it understand our business: Explain custom training capabilities\\n   - What about complex questions: Mention 98% accuracy and seamless human handoff\\n   - Implementation concerns: Discuss typical 2-4 week deployment\\n\\n4. GUIDE TO NEXT STEPS:\\n   - Interested in seeing it work: [View live demos](aivideos.html)\\n   - Want to discuss specific needs: [Book a consultation](calendar.html)\\n   - Want to explore solutions: [View our websites](websites.html)\\n   - Ready to talk: [Book a consultation](calendar.html)\\n\\n5. BOOKING DEMOS:\\n   When someone wants to book, guide them: 'I can help you schedule a consultation! We offer a FREE 15-minute discovery call to understand your needs, plus longer strategy sessions for deeper planning. Our free consultation is perfect for getting expert advice with no commitment required. Which sounds best for you?' Then direct to [Book your free consultation](calendar.html)\\n\\n=== CONVERSATION GUIDELINES ===\\n\\n- Be consultative, not pushy - focus on understanding their needs first\\n- Ask qualifying questions before recommending solutions\\n- Use real numbers: 70% cost reduction, <2s response, 98% accuracy\\n- Share relevant success stories when applicable\\n- Always offer multiple next steps (AI videos demo, websites, book consultation)\\n- If they ask technical questions you're unsure about, be honest and offer to connect them with a specialist\\n- Capture lead information when appropriate (name, company, email) for follow-up\\n\\n=== FORMATTING RULES ===\\n\\n- Write in natural, conversational paragraphs\\n- DO NOT use markdown headers (###) or excessive formatting\\n- Use bullet points sparingly, only when listing features or options\\n- When providing links, use format: [Link Text](filename.html)\\n- Keep responses concise but informative - aim for 2-4 paragraphs\\n- Use emojis sparingly for emphasis (âœ… âš¡ ðŸ’° ðŸ“Š)\\n\\n=== KEY PAGES ===\\n\\n- [Book a Free Consultation](calendar.html) - Schedule free discovery call\\n- [Live Demos](aivideos.html) - Interactive product demos by industry\\n- [Websites](websites.html) - Professional website solutions\\n- [Home](index.html) - Main landing page\\n\\nRemember: Your goal is to help prospects discover if AskMind is right for them, answer their questions honestly, and make it easy to take the next step. Be helpful, be consultative, and focus on their business needs."
            }]
        };

        const payload = {
            message: text,
            history: apiHistory
        };

        try {
            const response = await fetch('/.netlify/functions/ask-ai', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`API Error: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.reply) {
                const aiText = result.reply;

                const responseTime = Date.now() - startTime;

                // Add AI response
                const responseMsg = {
                    sender: 'ai',
                    type: 'text',
                    text: aiText,
                    timestamp: new Date().toISOString(),
                    responseTime: responseTime
                };

                setChatHistory(prev => [...prev, responseMsg]);
                
                // Auto-detect wizard triggers in AI responses
                setTimeout(() => {
                    const lowerResponse = aiText.toLowerCase();
                    if (lowerResponse.includes('report') && (lowerResponse.includes('bin') || lowerResponse.includes('collection'))) {
                        setTimeout(() => startWizard('bin-collection-report'), 2000);
                    } else if (lowerResponse.includes('parking permit') || lowerResponse.includes('apply') && lowerResponse.includes('permit')) {
                        setTimeout(() => startWizard('parking-permit'), 2000);
                    } else if (lowerResponse.includes('report') && (lowerResponse.includes('issue') || lowerResponse.includes('problem'))) {
                        setTimeout(() => startWizard('report-issue'), 2000);
                    }
                }, 1000);
            } else {
                throw new Error("No valid response from AI");
            }
        } catch (error) {
            console.error('AI Error:', error);
            

            
            let errorMessage = "Sorry, I'm having trouble connecting right now.\\n\\n";
            
            // Provide user-friendly error messages
            if (error.message.includes('429') || error.message.includes('rate limit')) {
                errorMessage += "I'm getting too many requests at the moment. Please wait a moment and try again.";
            } else if (error.message.includes('403') || error.message.includes('401')) {
                errorMessage += "There's an authentication issue with the AI service. Please try again in a few minutes.";
            } else if (error.name === 'TypeError' || error.message.includes('Failed to fetch')) {
                errorMessage += "I can't connect to the server right now. Please check your internet connection and try again.";
            } else if (error.message.includes('500')) {
                errorMessage += "There's a temporary server issue. Please try again in a few minutes.";
            } else {
                errorMessage += "Something unexpected happened. Please try asking your question again.";
            }
            
            errorMessage += "\\n\\nThis error has been logged for analysis.";
            
            const errorMsg = {
                sender: 'ai',
                type: 'error',
                text: errorMessage,
                canRetry: !error.message.includes('403') && !error.message.includes('401'),
                originalMessage: text
            };
            setChatHistory(prev => [...prev, errorMsg]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        handleSend(inputText);
    };

    // User Preferences Management
    const updatePreference = (key, value) => {
        const newPrefs = { ...userPrefs, [key]: value };
        setUserPrefs(newPrefs);
        localStorage.setItem('mind_user_preferences', JSON.stringify(newPrefs));
        
        // Apply language mode to initial message
        if (key === 'languageMode') {
            const friendlyGreeting = { 
                sender: 'ai', 
                type: 'text', 
                text: "Hi there! I'm Iris, your AI assistant from AskMind. I'm here to help you explore our products, answer any questions, and schedule a demo. Whether you're looking to automate customer service, streamline operations, or boost sales - I've got you covered. What would you like to know?" 
            };
            const professionalGreeting = { 
                sender: 'ai', 
                type: 'text', 
                text: "Hello! I'm Iris, AskMind's AI sales assistant. I can help you explore our enterprise AI solutions, discuss pricing and features, schedule personalized demos, and answer technical questions. What brings you here today?" 
            };
            
            setChatHistory(prev => {
                const newHistory = [...prev];
                newHistory[0] = value === 'friendly' ? friendlyGreeting : professionalGreeting;
                localStorage.setItem('mind_chat_history', JSON.stringify(newHistory));
                return newHistory;
            });
        }
    };

    // Auto-send message if query param exists
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const query = params.get('q');
        if (query) {
            // Clear the param from URL without reload
            window.history.replaceState({}, document.title, window.location.pathname);
            handleSend(query);
        }
    }, []);
    
    // Apply preferences whenever they change
    useEffect(() => {
        // Apply dark mode
        if (userPrefs.darkMode) {
            document.documentElement.classList.add('dark');
            document.body.style.backgroundColor = '#0f172a';
        } else {
            document.documentElement.classList.remove('dark');
            document.body.style.backgroundColor = '#f8fafc';
        }

        // Apply font size
        const fontSizes = { small: '14px', medium: '16px', large: '18px' };
        document.documentElement.style.fontSize = fontSizes[userPrefs.fontSize];
        
        // Save preferences
        localStorage.setItem('mind_user_preferences', JSON.stringify(userPrefs));
    }, [userPrefs]);

    return (
        <div className="flex flex-col h-full bg-slate-50">
            
            {/* Header removed - using site-wide header for consistency */}

            {/* Chat Area */}
            <main 
                ref={chatContainerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto p-4 scrollbar-hide"
            >
                <div className="w-full py-4 px-2">
                    
                    {/* Messages */}
                    {chatHistory.map((msg, idx) => (
                        <div key={idx} data-message-index={idx}>
                            <ChatMessage 
                                msg={msg} 
                                userPrefs={userPrefs}
                                onQuickAction={handleQuickAction}
                                onStartWizard={startWizard}
                                onSend={handleSend}
                            />
                        </div>
                    ))}

                    {/* Active Wizard Display */}
                    {activeWizard && (
                        <div className="flex justify-start mb-6">
                            <div className="w-8 h-8 rounded-full bg-white border border-indigo-100 flex items-center justify-center mr-3 shadow-sm shrink-0 mt-1">
                                <MINDLogo className="w-5 h-5" />
                            </div>
                            <div className="max-w-[85%] md:max-w-[70%]">
                                <WizardStep
                                    wizard={activeWizard.wizard}
                                    stepIndex={activeWizard.currentStep}
                                    wizardData={wizardData}
                                    onUpdate={updateWizardData}
                                    onNext={nextWizardStep}
                                    onPrev={prevWizardStep}
                                    onComplete={completeWizard}
                                    onCancel={cancelWizard}
                                />
                            </div>
                        </div>
                    )}

                    {/* Typing Indicator */}
                    {isTyping && (
                        <div className="flex justify-start mb-6">
                            <div className="w-8 h-8 rounded-full bg-white border border-indigo-100 flex items-center justify-center mr-3 shadow-sm shrink-0">
                                <MINDLogo className="w-5 h-5" />
                            </div>
                            <div className="bg-white p-4 rounded-2xl rounded-bl-none border border-indigo-50 shadow-sm flex items-center gap-1">
                                <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"></span>
                                <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{animationDelay: "0.2s"}}></span>
                                <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{animationDelay: "0.4s"}}></span>
                            </div>
                        </div>
                    )}
                    
                    <div ref={messagesEndRef} />
                </div>
            </main>

            {/* Footer / Input Area */}
            <footer className="flex-none bg-white border-t border-indigo-50 p-4 pb-6 z-20">
                <div className="w-full space-y-4">
                    
                    {/* Quick Suggestions removed for cleaner mobile experience */}

                    {/* Input Form */}
                    <form onSubmit={handleSubmit} className="relative flex items-center">
                        <input
                            type="text"
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            placeholder="Ask me anything..."
                            className="w-full h-14 pl-6 pr-14 bg-slate-50 border border-slate-200 rounded-full text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-inner text-base"
                            disabled={isTyping}
                            autoComplete="off"
                            autoCorrect="on"
                            autoCapitalize="sentences"
                            spellCheck="true"
                        />
                        <button 
                            type="submit" 
                            disabled={!inputText.trim() || isTyping}
                            aria-label="Send message"
                            className={`absolute right-2 top-2 h-10 w-10 flex items-center justify-center rounded-full transition-all ${
                                inputText.trim() 
                                    ? 'bg-iris-gradient text-white shadow-md hover:scale-105' 
                                    : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                            }`}
                        >
                            <SendIcon />
                        </button>
                    </form>
                    
                    <p className="text-center text-[10px] text-slate-500">
                        Powered by Iris v2.5. AI-assisted responses.
                    </p>
                </div>
            </footer>

            {/* Settings Panel */}
            <SettingsPanel 
                isOpen={isSettingsOpen} 
                onClose={() => setIsSettingsOpen(false)}
                preferences={userPrefs}
                onPreferenceChange={updatePreference}
            />

        </div>
    );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
    

